import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Star, Truck, Shield, Award, ChevronRight, Phone, Mail, Heart, Handshake, Check, Smartphone, Download, Wifi, Bell, Zap } from 'lucide-react';
import axios from 'axios';

const MERV_DATA = [
  {
    rating: 'MERV 8',
    color: 'sky',
    efficiency: '20%',
    label: 'Basic Protection',
    best: 'Standard residential use',
    captures: ['Dust & lint', 'Pollen', 'Dust mites', 'Pet dander'],
    doesNot: ['Mold spores', 'Fine particles', 'Bacteria'],
  },
  {
    rating: 'MERV 11',
    color: 'teal',
    efficiency: '65%',
    label: 'Enhanced Protection',
    best: 'Homes with pets or allergies',
    captures: ['Everything MERV 8 captures', 'Mold spores', 'Auto emissions', 'Smog particles'],
    doesNot: ['Bacteria', 'Smoke particles'],
  },
  {
    rating: 'MERV 13',
    color: 'emerald',
    efficiency: '90%',
    label: 'Superior Protection',
    best: 'Healthcare-grade air quality',
    captures: ['Everything MERV 11 captures', 'Bacteria', 'Smoke particles', 'Virus carriers', 'Fine PM2.5 particles'],
    doesNot: [],
  },
];

const AT1S_LOGO = '/at1s-logo-solid.png';
const AT1S_FILTER_IMAGE = '/at1s-filter.png';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("1");
  const [allSizes, setAllSizes] = useState({});
  const [tabs, setTabs] = useState([]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products/sizes`)
      .then(res => {
        const data = res.data;
        if (data && Object.keys(data).length > 0) {
          setAllSizes(data);
          const sortedTabs = Object.keys(data).sort((a, b) => Number(a) - Number(b));
          setTabs(sortedTabs);
          setActiveTab(sortedTabs[0]);
        } else {
          setAllSizes({});
          setTabs([]);
        }
      })
      .catch(() => {
        setAllSizes({});
        setTabs([]);
      });
  }, []);

  const handleSizeClick = (size) => {
    if (user) {
      navigate(`/customer/new-order?size=${size}`);
    } else {
      navigate(`/login?redirect=/customer/new-order?size=${size}`);
    }
  };

  // Calculate image scale based on filter dimensions (larger filters = bigger image)
  const getFilterScale = (sizeStr) => {
    const parts = sizeStr.split('X').map(Number);
    const w = parts[0] || 12, h = parts[1] || 12;
    const area = w * h;
    // Range: 100 (10x10) to 750 (25x30). Map to 75%-100%
    const minArea = 100, maxArea = 750;
    const pct = 75 + ((area - minArea) / (maxArea - minArea)) * 25;
    return Math.min(100, Math.max(75, Math.round(pct)));
  };

  const handleShopClick = (merv) => {
    if (user) {
      navigate(merv ? `/customer/new-order?merv=${merv}` : '/customer/new-order');
    } else {
      navigate(merv ? `/register?redirect=/customer/new-order?merv=${merv}` : '/register');
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Header */}
      <header className="bg-[#0a1628] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <img src={AT1S_LOGO} alt="AirTech OneSolutions" className="h-10 w-auto object-contain" data-testid="header-logo" />
            <div className="flex items-center gap-3">
              {user ? (
                <button
                  onClick={() => navigate(user.role === 'admin' ? '/dashboard' : '/customer/dashboard')}
                  className="text-sm font-medium text-white/80 hover:text-white transition-colors"
                  data-testid="my-account-btn"
                >
                  My Account
                </button>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm font-medium text-white/80 hover:text-white transition-colors"
                  data-testid="sign-in-btn"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-[#0a1628] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-900/30 to-teal-900/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 md:py-16">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-1 mb-3 sm:mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-amber-400 text-amber-400" />
                ))}
                <span className="text-white/70 text-xs sm:text-sm ml-2">Trusted by thousands</span>
              </div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight mb-1">
                Professional Grade
              </h1>
              <h2 className="text-base sm:text-lg md:text-xl font-bold mb-3 sm:mb-4">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-teal-400">Replacement Filters</span>
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-white/70 mb-4 sm:mb-6 max-w-lg mx-auto md:mx-0">
                Residential and commercial disposable filters delivered to your door. Free shipping on orders over $100.
              </p>
              <button
                onClick={() => handleShopClick()}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-semibold px-6 sm:px-8 py-3 sm:py-3.5 rounded-lg text-base sm:text-lg transition-all shadow-lg shadow-sky-500/25"
                data-testid="shop-filters-btn"
              >
                Shop Air Filters <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-shrink-0 hidden md:block relative">
              <img src="/at1s-hero.png" alt="AirTech OneSolutions - Clean Air One Solutions" className="w-[750px] h-auto object-contain rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="py-8 sm:py-12 md:py-16 bg-gray-50 border-b border-gray-100" data-testid="product-gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2">
            Our Premium Air Filters
          </h2>
          <p className="text-sm sm:text-base text-gray-500 text-center mb-6 sm:mb-10">
            Professional-grade MERV-rated pleated filters — manufactured for performance
          </p>

          <div className="flex justify-center" data-testid="product-gallery-row-1">
            <div className="relative rounded-2xl overflow-hidden shadow-lg max-w-2xl w-full bg-white" data-testid="product-image-0">
              <img
                src={AT1S_FILTER_IMAGE}
                alt="AT1S MERV 13 Pleated Air Filters"
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Size Selection - Visual Product Cards */}
      {tabs.length > 0 ? (
        <section className="py-8 sm:py-12 md:py-16 bg-white" data-testid="size-selection-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2">
              Choose Your Air Filter Size
            </h2>
            <p className="text-sm sm:text-base text-gray-500 text-center mb-6 sm:mb-8">Select a thickness, then click your filter to start ordering</p>

            {/* Thickness Tabs */}
            <div className="flex justify-center gap-1.5 sm:gap-2 mb-6 sm:mb-8 flex-wrap" data-testid="thickness-tabs">
              {tabs.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-semibold text-xs sm:text-sm transition-all ${
                    activeTab === tab
                      ? 'bg-[#0a1628] text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`tab-${tab}-inch`}
                >
                  {tab}" Filters
                </button>
              ))}
            </div>

            {/* Visual Product Cards Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3" data-testid="size-grid">
              {allSizes[activeTab]?.slice(0, 12).map(size => {
                const scale = getFilterScale(size);
                return (
                  <button
                    key={size}
                    onClick={() => handleSizeClick(size)}
                    className="group relative bg-white rounded-xl border border-gray-200 hover:border-teal-400 hover:shadow-lg transition-all overflow-hidden text-left"
                    data-testid={`size-btn-${size}`}
                  >
                    <div className="relative aspect-square overflow-hidden flex items-center justify-center">
                      <img
                        src={AT1S_FILTER_IMAGE}
                        alt={`AT1S Air Filter ${size}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="px-2 py-2 border-t border-gray-100">
                      <p className="text-xs font-semibold text-gray-900 group-hover:text-teal-700 transition-colors truncate">{size}</p>
                      <span className="text-[10px] text-gray-400">MERV 8/11/13</span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* View Full Catalog Link */}
            {(allSizes[activeTab]?.length || 0) > 12 && (
              <div className="text-center mt-6">
                <span className="text-sm text-gray-400">
                  Showing 12 of {allSizes[activeTab]?.length} sizes in {activeTab}" thickness
                </span>
              </div>
            )}
            <div className="text-center mt-4">
              <button
                onClick={() => navigate('/catalog')}
                className="inline-flex items-center gap-2 bg-[#0a1628] hover:bg-[#0f2240] text-white font-semibold px-6 py-3 rounded-lg text-sm transition-all"
                data-testid="view-full-catalog-btn"
              >
                View Full Product Catalog <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-12 md:py-16 bg-white" data-testid="size-selection-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Our Filter Catalog</h2>
            <p className="text-gray-500 mb-4">Product catalog is being set up. Contact us for available sizes and pricing.</p>
            <div className="flex justify-center gap-3">
              <a href="mailto:contactat1s@air-techsolutions.com" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0a1628] text-white rounded-lg font-medium text-sm hover:bg-[#0f2240] transition-colors">
                <Mail className="w-4 h-4" /> Contact Us
              </a>
              <a href="tel:+1" className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50 transition-colors">
                <Phone className="w-4 h-4" /> Call Now
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Value Props */}
      <section className="py-12 bg-gray-50 border-y border-gray-100" data-testid="value-props-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-sky-100 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-sky-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">MERV 8, 11 & 13 Ratings</h3>
              <p className="text-sm text-gray-500">Multiple filtration levels to match your air quality needs and HVAC system requirements.</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <Truck className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Free Shipping Over $100</h3>
              <p className="text-sm text-gray-500">Fast, reliable delivery straight to your door. No hassle, no hidden fees.</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <Award className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Premium Quality Filters</h3>
              <p className="text-sm text-gray-500">Professional-grade pleated filters designed for superior air filtration performance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* MERV Rating Comparison */}
      <section className="py-8 sm:py-12 md:py-16 bg-white" data-testid="merv-comparison-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2">MERV Rating Comparison</h2>
          <p className="text-sm sm:text-base text-gray-500 text-center mb-6 sm:mb-10">Choose the right filtration level for your home or business</p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {MERV_DATA.map((merv) => {
              const borderColor = merv.color === 'sky' ? 'border-sky-200' : merv.color === 'teal' ? 'border-teal-200' : 'border-emerald-200';
              const headerBg = merv.color === 'sky' ? 'bg-sky-600' : merv.color === 'teal' ? 'bg-teal-600' : 'bg-emerald-600';
              const badgeBg = merv.color === 'sky' ? 'bg-sky-100 text-sky-700' : merv.color === 'teal' ? 'bg-teal-100 text-teal-700' : 'bg-emerald-100 text-emerald-700';
              const checkColor = merv.color === 'sky' ? 'text-sky-500' : merv.color === 'teal' ? 'text-teal-500' : 'text-emerald-500';

              return (
                <div key={merv.rating} className={`rounded-xl border-2 ${borderColor} overflow-hidden flex flex-col`} data-testid={`merv-card-${merv.rating.replace(' ', '-')}`}>
                  <div className={`${headerBg} text-white px-5 py-4 text-center`}>
                    <p className="text-2xl font-bold">{merv.rating}</p>
                    <p className="text-sm text-white/80">{merv.label}</p>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="text-center mb-4">
                      <p className="text-3xl font-bold text-gray-900">{merv.efficiency}</p>
                      <p className="text-xs text-gray-400">Particle Capture Rate</p>
                    </div>
                    <div className={`text-center text-xs font-semibold ${badgeBg} rounded-full px-3 py-1 mb-4 mx-auto`}>
                      Best for: {merv.best}
                    </div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Captures:</p>
                    <ul className="space-y-1.5 mb-4 flex-1">
                      {merv.captures.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${checkColor}`} />
                          {item}
                        </li>
                      ))}
                    </ul>
                    {merv.doesNot.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Does not capture:</p>
                        <p className="text-xs text-gray-400">{merv.doesNot.join(', ')}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advertising Section */}
      <section className="py-8 sm:py-12 md:py-16 bg-gray-50 border-y border-gray-100" data-testid="ad-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">Why Filter Replacement Matters</h2>
            <p className="text-sm sm:text-base text-gray-500">A clogged filter forces your HVAC system to work harder — costing you more and polluting your air</p>
          </div>
          <div className="max-w-2xl mx-auto">
            {/* AT1S Filter Product Card */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg group" data-testid="filter-card">
              <img
                src={AT1S_FILTER_IMAGE}
                alt="AT1S MERV 13 Disposable Air Filter"
                className="w-full h-48 sm:h-72 object-cover group-hover:scale-105 transition-transform duration-500"
                data-testid="filter-product-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" style={{ zIndex: 6 }} />
              <div className="absolute bottom-0 left-0 p-6" style={{ zIndex: 7 }}>
                <p className="text-white font-bold text-lg">Replace Filters Regularly</p>
                <p className="text-white/70 text-sm">Dirty filters restrict airflow, increase energy bills, and reduce air quality</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us */}
      <section className="py-10 sm:py-16 md:py-20 bg-gray-50 border-y border-gray-100" data-testid="about-us-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-10">
            <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-700 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4">
              <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> Our Story
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">About AirTech OneSolutions</h2>
          </div>
          <div className="bg-white rounded-2xl p-5 sm:p-8 md:p-10 shadow-sm border border-gray-100">
            <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed mb-4 sm:mb-6">
              Founded by Rosh and Mathew, AirTech OneSolutions was born from a simple, heartfelt vision: the belief that every family deserves to breathe air that is fresh, pure, and safe. As a manufacturer and distributor of residential and commercial air filters, they don't just see a product — they see a commitment to the health of our community and our world.
            </p>
            <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
              <div className="w-10 h-10 bg-[#0a1628] rounded-full flex items-center justify-center">
                <Handshake className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Rosh & Mathew</p>
                <p className="text-xs text-gray-500">Co-Founders</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-8 sm:py-12 bg-[#0a1628]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">Ready to Order?</h2>
          <p className="text-sm sm:text-base text-white/60 mb-4 sm:mb-6">Choose from every size. Free shipping on orders over $100.</p>
          <button
            onClick={() => handleShopClick()}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-semibold px-8 py-3 rounded-lg text-lg transition-all"
            data-testid="cta-shop-btn"
          >
            Shop Air Filters <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Download App Section */}
      <DownloadAppSection />

      {/* Footer */}
      <footer className="bg-[#060e1a] text-white/60 py-10" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <img src={AT1S_LOGO} alt="AirTech OneSolutions" className="h-10 w-auto object-contain mb-3 mx-auto md:mx-0" />
              <p className="text-sm">Professional-grade residential and commercial disposable filters.</p>
            </div>
            <div className="flex flex-col items-center md:items-end gap-2 text-sm">
              <a href="mailto:contactat1s@air-techsolutions.com" className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="w-4 h-4" /> contactat1s@air-techsolutions.com
              </a>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" /> +1 832 835 7362 | +1 832 228 7463
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-6 pt-6 text-center text-xs text-white/40">
            &copy; {new Date().getFullYear()} AirTech OneSolutions. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

const DownloadAppSection = () => {
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [installed, setInstalled] = React.useState(false);

  React.useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
      setInstalled(true);
      return;
    }
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
  };

  return (
    <section className="py-12 sm:py-16 bg-gradient-to-br from-slate-50 to-sky-50 border-y border-slate-200" data-testid="download-app-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-700 rounded-full px-4 py-1.5 text-xs font-semibold mb-4">
            <Smartphone className="w-3.5 h-3.5" /> Available on all devices
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-3">
            Get the AT1S App
          </h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            Order filters, track deliveries, and manage your account — all from your phone. Install in seconds, no app store needed.
          </p>
        </div>

        {/* Features row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10 max-w-2xl mx-auto">
          {[
            { icon: Zap, text: 'Instant access', sub: 'No download wait' },
            { icon: Bell, text: 'Order updates', sub: 'Real-time status' },
            { icon: Wifi, text: 'Works offline', sub: 'Cached pages' },
            { icon: Shield, text: 'Secure', sub: 'Same encryption' },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-2.5 bg-white rounded-xl p-3 shadow-sm border border-slate-100">
              <div className="bg-sky-50 rounded-lg p-1.5 shrink-0">
                <f.icon className="w-4 h-4 text-sky-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">{f.text}</p>
                <p className="text-xs text-slate-500">{f.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Android install button (if available) */}
        {deferredPrompt && (
          <div className="text-center mb-10">
            <button
              onClick={handleInstall}
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 text-white font-bold px-10 py-4 rounded-xl text-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.98]"
              data-testid="download-install-btn"
            >
              <Download className="w-5 h-5" /> Install App Now
            </button>
          </div>
        )}

        {installed && (
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 rounded-xl px-6 py-3 font-semibold">
              <Check className="w-5 h-5" /> App already installed
            </div>
          </div>
        )}

        {/* Visual Step-by-Step Guide — Tabbed for iPhone & Android */}
        <InstallGuide installed={installed} deferredPrompt={deferredPrompt} handleInstall={handleInstall} />
      </div>
    </section>
  );
};

const InstallGuide = ({ installed, deferredPrompt, handleInstall }) => {
  const [platform, setPlatform] = React.useState('iphone');

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden" data-testid="install-steps-guide">
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
        <h3 className="text-white font-bold text-lg flex items-center gap-2">
          <Smartphone className="w-5 h-5" /> How to Install on Your Phone
        </h3>
        <p className="text-blue-200 text-sm mt-1">Follow these simple steps — takes 10 seconds</p>
      </div>

      {/* Platform tabs */}
      <div className="flex border-b border-slate-200">
        <button
          onClick={() => setPlatform('iphone')}
          className={`flex-1 py-3 px-4 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${platform === 'iphone' ? 'text-blue-700 border-b-2 border-blue-700 bg-blue-50' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-iphone"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="3" /><line x1="12" y1="18" x2="12" y2="18.01" strokeWidth="3" /></svg>
          iPhone / iPad
        </button>
        <button
          onClick={() => setPlatform('android')}
          className={`flex-1 py-3 px-4 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${platform === 'android' ? 'text-green-700 border-b-2 border-green-700 bg-green-50' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-android"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2" /><line x1="9" y1="19" x2="15" y2="19" /></svg>
          Android
        </button>
      </div>

      <div className="p-6 sm:p-8">
        {platform === 'iphone' ? (
          /* ── iPhone Steps ── */
          <div className="space-y-0">
            <StepRow num={1} color="sky" title="Open in Safari"
              desc={<>Visit <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-sky-700 text-xs">air-techsolutions.com</span> in <strong>Safari</strong> on your iPhone</>}
              note="Must use Safari — Chrome/Firefox on iPhone don't support this"
              visual={
                <div className="w-14 h-14 bg-gradient-to-br from-sky-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-md">
                  <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10" /><polygon points="12,2 15,10 12,22 9,10" fill="currentColor" opacity="0.3" />
                    <line x1="12" y1="2" x2="12" y2="22" /><line x1="2" y1="12" x2="22" y2="12" />
                  </svg>
                </div>
              }
            />
            <StepRow num={2} color="sky" title="Tap the Share Button"
              desc={<>At the <strong>bottom center</strong> of Safari — square with an arrow pointing up</>}
              visual={
                <div className="bg-slate-100 rounded-2xl p-3 flex flex-col items-center gap-2">
                  <div className="w-12 h-12 bg-white rounded-xl border-2 border-sky-500 flex items-center justify-center shadow-sm">
                    <svg viewBox="0 0 24 24" className="w-7 h-7 text-sky-600" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <rect x="4" y="10" width="16" height="12" rx="2" /><path d="M12 2v12" /><path d="M8 6l4-4 4 4" />
                    </svg>
                  </div>
                  <span className="text-[10px] font-bold text-sky-700 bg-sky-50 px-2 py-0.5 rounded-full">Tap this</span>
                </div>
              }
            />
            <StepRow num={3} color="sky" title='Tap "Add to Home Screen"'
              desc="In the share sheet, scroll down to find this option"
              visual={
                <div className="bg-slate-100 rounded-2xl p-3 w-52">
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-3 py-1.5 border-b border-slate-100 flex items-center gap-2 opacity-40"><div className="w-5 h-5 bg-slate-200 rounded" /><span className="text-[11px] text-slate-400">Copy</span></div>
                    <div className="px-3 py-2 flex items-center gap-2 bg-sky-50 border-2 border-sky-400 rounded-lg my-1 mx-1">
                      <div className="w-5 h-5 bg-sky-500 rounded flex items-center justify-center">
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                      </div>
                      <span className="text-[11px] font-bold text-sky-700">Add to Home Screen</span>
                    </div>
                    <div className="px-3 py-1.5 flex items-center gap-2 opacity-40"><div className="w-5 h-5 bg-slate-200 rounded" /><span className="text-[11px] text-slate-400">Add to Reading List</span></div>
                  </div>
                </div>
              }
            />
            <StepRow num={4} color="green" title='Tap "Add" — Done!' last
              desc="The AT1S app icon appears on your home screen. Open it anytime!"
              visual={<AppIconVisual />}
            />
          </div>
        ) : (
          /* ── Android Steps ── */
          <div className="space-y-0">
            {deferredPrompt ? (
              /* Chrome auto-prompt available */
              <>
                <StepRow num={1} color="green" title="Tap Install Below"
                  desc="Chrome will show a native install dialog automatically"
                  visual={
                    <button onClick={handleInstall} className="bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold px-5 py-3 rounded-xl shadow-lg text-sm flex items-center gap-2 hover:shadow-xl active:scale-[0.98] transition-all" data-testid="android-install-btn">
                      <Download className="w-4 h-4" /> Install Now
                    </button>
                  }
                />
                <StepRow num={2} color="green" title='Tap "Install" in the popup' last
                  desc="Chrome shows a confirmation — tap Install and the app is added to your home screen"
                  visual={<AppIconVisual />}
                />
              </>
            ) : (
              /* Manual Chrome steps */
              <>
                <StepRow num={1} color="green" title="Open in Chrome"
                  desc={<>Visit <span className="font-mono bg-slate-100 px-2 py-0.5 rounded text-green-700 text-xs">air-techsolutions.com</span> in <strong>Chrome</strong> on your Android phone</>}
                  visual={
                    <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center shadow-md">
                      <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="4" />
                        <line x1="12" y1="2" x2="12" y2="8" /><line x1="4.93" y1="4.93" x2="9.17" y2="9.17" /><line x1="2" y1="12" x2="8" y2="12" />
                      </svg>
                    </div>
                  }
                />
                <StepRow num={2} color="green" title="Tap the Three Dots Menu"
                  desc={<>Tap the <strong>three vertical dots</strong> at the <strong>top right</strong> of Chrome</>}
                  visual={
                    <div className="bg-slate-100 rounded-2xl p-3 flex flex-col items-center gap-2">
                      <div className="w-12 h-12 bg-white rounded-xl border-2 border-green-500 flex flex-col items-center justify-center gap-1 shadow-sm">
                        <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />
                        <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />
                        <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />
                      </div>
                      <span className="text-[10px] font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Tap this</span>
                    </div>
                  }
                />
                <StepRow num={3} color="green" title='Tap "Add to Home Screen" or "Install App"'
                  desc="In the menu, look for either option (wording varies by Chrome version)"
                  visual={
                    <div className="bg-slate-100 rounded-2xl p-3 w-52">
                      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                        <div className="px-3 py-1.5 border-b border-slate-100 flex items-center gap-2 opacity-40"><span className="text-[11px] text-slate-400">New tab</span></div>
                        <div className="px-3 py-1.5 border-b border-slate-100 flex items-center gap-2 opacity-40"><span className="text-[11px] text-slate-400">Bookmarks</span></div>
                        <div className="px-3 py-2 flex items-center gap-2 bg-green-50 border-2 border-green-400 rounded-lg my-1 mx-1">
                          <Download className="w-4 h-4 text-green-600" />
                          <span className="text-[11px] font-bold text-green-700">Install App</span>
                        </div>
                        <div className="px-3 py-1.5 flex items-center gap-2 opacity-40"><span className="text-[11px] text-slate-400">Settings</span></div>
                      </div>
                    </div>
                  }
                />
                <StepRow num={4} color="green" title='Tap "Install" — Done!' last
                  desc="The AT1S app icon appears on your home screen. Open it anytime!"
                  visual={<AppIconVisual />}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StepRow = ({ num, color, title, desc, note, visual, last }) => (
  <div className={`flex flex-col sm:flex-row gap-5 items-center py-6 ${!last ? 'border-b border-slate-100' : ''}`}>
    <div className={`shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center ${color === 'green' ? 'bg-green-100' : 'bg-sky-100'}`}>
      <span className={`text-2xl font-black ${color === 'green' ? 'text-green-700' : 'text-sky-700'}`}>{num}</span>
    </div>
    <div className="flex-1 text-center sm:text-left">
      <h4 className="font-bold text-slate-900 text-lg mb-1">{title}</h4>
      <p className="text-slate-600 text-sm">{desc}</p>
      {note && <p className="text-xs text-amber-600 mt-1 font-medium">{note}</p>}
    </div>
    <div className="shrink-0">{visual}</div>
  </div>
);

const AppIconVisual = () => (
  <div className="bg-slate-100 rounded-2xl p-4 flex flex-col items-center gap-2">
    <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-blue-700 rounded-2xl flex items-center justify-center shadow-lg">
      <span className="text-white text-xs font-black">AT1S</span>
    </div>
    <span className="text-[10px] text-slate-600 font-medium">AirTech</span>
    <div className="flex items-center gap-1">
      <Check className="w-3 h-3 text-green-500" />
      <span className="text-[10px] text-green-600 font-semibold">Installed</span>
    </div>
  </div>
);

export default LandingPage;
